import React, { useEffect, useState } from 'react';
import ReportNote from '../components/ReportNote';

const API = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

import { localDateStr, weekMonday, fmtDate } from './utils';

function TrendChart({ trend, currentWeek }) {
  if (!trend || trend.length === 0) return null;
  const max = Math.max(...trend.map(w => w.tickets), 1);
  const n = trend.length;
  const showLabel = (i, w) => w.week_start === currentWeek || i % 4 === 0;

  return (
    <div style={{ width: '100%' }}>
      {/* Bars — all share the same bottom baseline via alignItems:'flex-end' */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 120 }}>
        {trend.map((w, i) => {
          const isCurrent = w.week_start === currentWeek;
          const pct = Math.max((w.tickets / max) * 100, w.tickets > 0 ? 2 : 0.5);
          const color = isCurrent ? 'var(--accent)' : '#94a3b8';
          return (
            <div key={w.week_start} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%', position: 'relative' }}>
              {(isCurrent || (w.tickets > 0 && n <= 20)) && (
                <span style={{ position: 'absolute', bottom: `calc(${pct}% + 3px)`, left: '50%', transform: 'translateX(-50%)', fontSize: 9, fontWeight: isCurrent ? 700 : 400, color, lineHeight: 1, whiteSpace: 'nowrap' }}>{w.tickets}</span>
              )}
              <div style={{ width: '75%', height: `${pct}%`, background: color, opacity: isCurrent ? 1 : 0.55, borderRadius: '2px 2px 0 0' }} />
            </div>
          );
        })}
      </div>
      {/* Labels row — separate from bars so they don't affect alignment */}
      <div style={{ display: 'flex', gap: 2, marginTop: 5 }}>
        {trend.map((w, i) => {
          const isCurrent = w.week_start === currentWeek;
          return (
            <div key={w.week_start} style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              {showLabel(i, w) && (
                <span style={{ fontSize: 9, color: isCurrent ? 'var(--text)' : 'var(--text-3)', fontWeight: isCurrent ? 700 : 400, whiteSpace: 'nowrap', lineHeight: 1 }}>{w.label}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function WeeklyReport() {
  const [weekOffset, setWeekOffset] = useState(0);
  const [weekStart, setWeekStart] = useState(weekMonday(0));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trend, setTrend] = useState(null);

  const load = (ws) => {
    setLoading(true);
    fetch(`${API}/api/reports/weekly?week_start=${ws}`)
      .then(r => r.json()).then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    fetch(`${API}/api/reports/weekly/trend`)
      .then(r => r.json()).then(setTrend).catch(() => {});
  }, []);

  useEffect(() => {
    const ws = weekMonday(weekOffset);
    setWeekStart(ws); load(ws);
  }, [weekOffset]);

  const exportCSV = () => {
    window.location.href = `${API}/api/reports/export/csv?type=weekly&week_start=${weekStart}`;
  };

  const maxTickets = data ? Math.max(...data.members.map(m => m.tickets), 1) : 1;

  return (
    <div>
      <ReportNote what="Tickets handled and time logged by each team member." weekStart={weekStart} />
      {/* Controls */}
      <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => setWeekOffset(o => o - 1)} className="btn btn-secondary" style={{ padding: '6px 12px' }}>‹</button>
          <span style={{ fontWeight: 700, fontSize: 15, minWidth: 200, textAlign: 'center' }}>
            {data ? data.week_label : weekStart}
          </span>
          <button onClick={() => setWeekOffset(o => o + 1)} className="btn btn-secondary" style={{ padding: '6px 12px' }}>›</button>
        </div>
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <button onClick={exportCSV} className="btn btn-secondary">↓ CSV</button>
          <button onClick={() => window.print()} className="btn btn-primary">🖨 Print / PDF</button>
        </div>
      </div>

      {loading && <div className="loading">Loading weekly summary…</div>}

      {!loading && data && (
        <div className="print-area">
          {/* Print header */}
          <div style={{ marginBottom: 28 }}>
            <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 24, fontWeight: 800, color: 'var(--text)', marginBottom: 4 }}>
              Weekly Team Report
            </div>
            <div style={{ fontSize: 14, color: 'var(--text-2)' }}>
              Week of {data.week_label} · Artemys Belgium IT Team · Generated {fmtDate(localDateStr(new Date()))}
            </div>
          </div>

          {/* KPI row */}
          <div className="grid-4" style={{ marginBottom: 24 }}>
            <div className="card" style={{ borderLeft: '3px solid var(--accent)' }}>
              <div className="card-title">Tickets Resolved</div>
              <div className="card-value" style={{ color: 'var(--accent)' }}>{data.team_tickets}</div>
              <div className="card-sub">this week</div>
            </div>
            <div className="card" style={{ borderLeft: '3px solid var(--teal)' }}>
              <div className="card-title">Hours Logged</div>
              <div className="card-value" style={{ color: 'var(--teal)', fontSize: 22 }}>{data.team_hours_display}</div>
              <div className="card-sub">excl. ABE & absence</div>
            </div>
            <div className="card" style={{ borderLeft: '3px solid var(--critical)' }}>
              <div className="card-title">Open Incidents</div>
              <div className="card-value" style={{ color: data.open_incidents > 3 ? 'var(--critical)' : 'var(--text)' }}>
                {data.open_incidents}
              </div>
              <div className="card-sub">current</div>
            </div>
            <div className="card" style={{ borderLeft: '3px solid var(--ok)' }}>
              <div className="card-title">Week Capacity</div>
              <div className="card-value" style={{ fontSize: 22 }}>{data.cap_hours}h</div>
              <div className="card-sub">
                {data.holidays.length > 0
                  ? `${data.holidays.length} holiday(s) this week`
                  : 'per person · no holidays'}
              </div>
            </div>
          </div>

          {/* Trend chart */}
          {trend && (
            <div className="card no-print" style={{ marginBottom: 16, padding: '16px 20px' }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Tickets résolus — {new Date().getFullYear()} (année en cours)
              </div>
              <TrendChart trend={trend} currentWeek={weekStart} />
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            {/* Team breakdown */}
            <div className="card">
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16, fontFamily: 'Syne, sans-serif' }}>
                Team Breakdown
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', fontSize: 10, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.07em', paddingBottom: 8, borderBottom: '1px solid var(--border)' }}>Name</th>
                    <th style={{ textAlign: 'right', fontSize: 10, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.07em', paddingBottom: 8, borderBottom: '1px solid var(--border)' }}>Tickets</th>
                    <th style={{ textAlign: 'right', fontSize: 10, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.07em', paddingBottom: 8, borderBottom: '1px solid var(--border)' }}>Hours</th>
                    <th style={{ textAlign: 'right', fontSize: 10, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.07em', paddingBottom: 8, borderBottom: '1px solid var(--border)' }}>Util.</th>
                  </tr>
                </thead>
                <tbody>
                  {data.members.map(m => {
                    const color = m.utilization >= 95 ? 'var(--critical)' : m.utilization >= 80 ? 'var(--warn)' : 'var(--ok)';
                    return (
                      <tr key={m.name}>
                        <td style={{ padding: '8px 0', fontSize: 13, fontWeight: 600 }}>{m.name.split(' ')[0]} {m.name.split(' ').slice(-1)[0]}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', fontWeight: 700, color: 'var(--accent)', padding: '8px 0' }}>
                          {m.tickets}
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color: 'var(--text-2)', padding: '8px 0', fontSize: 12 }}>
                          {m.hours_display}
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', color, fontWeight: 700, padding: '8px 0', fontSize: 12 }}>
                          {m.utilization}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {/* Mini utilization bars */}
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {data.members.map(m => {
                  const color = m.utilization >= 95 ? 'var(--critical)' : m.utilization >= 80 ? 'var(--warn)' : 'var(--ok)';
                  return (
                    <div key={m.name} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 10, width: 70, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {m.name.split(' ')[0]}
                      </span>
                      <div style={{ flex: 1, height: 4, background: 'var(--border)', borderRadius: 99, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${Math.min(m.utilization, 100)}%`, background: color, borderRadius: 99 }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Top customers */}
            <div className="card">
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16, fontFamily: 'Syne, sans-serif' }}>
                Top Customers This Week
              </div>
              {data.top_customers.length === 0 ? (
                <div style={{ color: 'var(--text-2)', fontSize: 13, fontStyle: 'italic' }}>No customer tickets this week</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {data.top_customers.map((c, i) => (
                    <div key={c.project} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 11, color: 'var(--text-3)', width: 16 }}>{i + 1}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                        <div style={{ height: 4, background: 'var(--border)', borderRadius: 99, overflow: 'hidden', marginTop: 4 }}>
                          <div style={{ height: '100%', width: `${(c.tickets / (data.top_customers[0]?.tickets || 1)) * 100}%`, background: 'var(--accent)', borderRadius: 99 }} />
                        </div>
                      </div>
                      <span style={{ fontFamily: 'DM Mono, monospace', fontWeight: 700, fontSize: 15, color: 'var(--accent)' }}>{c.tickets}</span>
                    </div>
                  ))}
                </div>
              )}

              {data.holidays.length > 0 && (
                <div style={{ marginTop: 20, padding: '10px 14px', background: 'var(--warn-light)', borderRadius: 8, border: '1px solid var(--warn)', fontSize: 12 }}>
                  <strong style={{ color: 'var(--warn)' }}>Public holiday(s):</strong>{' '}
                  <span style={{ color: 'var(--text-2)' }}>{data.holidays.join(', ')}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @media print {
          .no-print { display: none !important; }
          .sidebar, .topbar { display: none !important; }
          .main { margin: 0 !important; padding: 20px !important; }
          .grid-4 { grid-template-columns: repeat(4, 1fr) !important; }
        }
      `}</style>
    </div>
  );
}
